import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/design-system/DropdownMenu'
import { Avatar } from '@/design-system/Avatar'
import { VerifiedBadge } from '@/components/auth/VerifiedBadge'
import { useAuth } from '@/hooks/useAuth'
import { useRole } from '@/hooks/useRole'
import { useCurrentProfile } from '@/lib/api/useCurrentProfile'
import { User, Settings, LogOut, Briefcase } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  UserMenu — top-right dropdown for authenticated users              */
/*  Profile link, settings, sign out                                    */
/*  Focus trap via Radix DropdownMenu                                   */
/*  See ARCHITECTURE.md §17, PROMPT-3-083                               */
/* ------------------------------------------------------------------ */

export function UserMenu() {
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const { role } = useRole()
  const { data: profile } = useCurrentProfile()

  const handleSignOut = async () => {
    await signOut()
    navigate('/', { replace: true })
  }

  const displayName = profile?.full_name || 'Konto'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          aria-label="Kontomeny"
        >
          <Avatar
            name={displayName}
            src={profile?.avatar_url}
            size="xs"
          />
          <span className="hidden lg:inline">{displayName}</span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {/* Profile info header */}
        <div className="px-2.5 py-2">
          <p className="text-sm font-medium text-neutral-900 inline-flex items-center gap-2">
            {displayName}
            <VerifiedBadge verified={profile?.bankid_verified === true} size="icon" />
          </p>
          {profile?.email && (
            <p className="text-xs text-neutral-500">{profile.email}</p>
          )}
        </div>

        <DropdownMenuSeparator />

        {role === 'provider' ? (
          <>
            <DropdownMenuItem
              icon={<Briefcase className="h-4 w-4" />}
              onSelect={() => navigate('/leverantor-portal')}
            >
              Kontrollpanel
            </DropdownMenuItem>

            <DropdownMenuItem
              icon={<User className="h-4 w-4" />}
              onSelect={() => navigate('/leverantor-portal/profil')}
            >
              Profil
            </DropdownMenuItem>

            <DropdownMenuItem
              icon={<Settings className="h-4 w-4" />}
              onSelect={() => navigate('/leverantor-portal/installningar')}
            >
              Inställningar
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem
              icon={<User className="h-4 w-4" />}
              onSelect={() => navigate('/dashboard')}
            >
              Min sida
            </DropdownMenuItem>

            <DropdownMenuItem
              icon={<Settings className="h-4 w-4" />}
              onSelect={() => navigate('/dashboard/installningar')}
            >
              Inställningar
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          icon={<LogOut className="h-4 w-4" />}
          onSelect={handleSignOut}
        >
          Logga ut
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

UserMenu.displayName = 'UserMenu'
